import type { MapData } from '../types/map';

/**
 * Helpers PUROS para detectar agua NAVEGABLE en un mapa.
 * Compartidos entre server (MapSystem.waterIndex → reglas de movimiento) y
 * cliente (GameScene.waterTiles → client-side prediction). Ver docs/WATER_AND_BOATS.md.
 *
 * Modelo (formato que emite el World Builder, ver shared/world/worldGenerator.ts):
 *  - El agua vive PER-TILE (sprite `water_1`, 1×1) en la capa de suelo `layer1_ground`.
 *  - La COSTA es un bloque de tierra que bordea agua: lleva agua per-tile en
 *    `layer1_ground` + un overlay 9-slice del bioma (sprite de bloque 4×4 con degradado
 *    hacia el agua) encima en `layer2_floor_objects`.
 *
 * Granularidad: los overlays de costa de los atlases de bioma (pattern1/3/4) NO marcan
 * el bloque entero como tierra — usan una MÁSCARA POR TILE derivada del alpha real del
 * sprite (tile = tierra si su celda de 32×32 tiene ≥50% de cobertura). Así el borde
 * caminable coincide con el borde VISUAL del pasto/arena/nieve, y la mitad "agua" del
 * degradado cuenta como agua navegable. El resto de las superficies (centros sólidos,
 * adoquín, pisos de sector, sprites desconocidos) marcan su bloque 4×4 completo.
 */

/** Lado del bloque de superficie en tiles (bloques 4×4 del World Builder). */
export const SURFACE_BLOCK = 4;

/** Capa de suelo donde vive el agua per-tile. */
const WATER_GROUND_LAYER = 'layer1_ground';

/**
 * Capas cuyas superficies no-agua marcan tierra/orilla. Mismo set que
 * `isShoreSurfaceLayer` del cliente (render del oleaje).
 */
const SHORE_SURFACE_LAYERS: readonly string[] = [
  'ground',
  'layer1_ground',
  'ground_deco_under',
  'layer2_floor_objects',
];

/** Atlases de bioma cuyos 9-slice de costa tienen degradado (pasto/arena/nieve). */
const COAST_ATLAS_RE = /^(?:pattern1|pattern3|pattern4)_(\d+)_(\d+)_4x4$/;

/**
 * Máscara de TIERRA por offset 9-slice, medida del alpha real de los atlases
 * (los tres comparten la misma estructura). Filas y=0..3, 'X' = el tile cuenta
 * como tierra (cobertura ≥50%), '.' = sigue siendo agua. Convención de offsets
 * en `NINE` (shared/world/worldGenerator.ts): borde = de qué lado está el agua;
 * mordidas (12/20_*) = esquina cóncava con agua en esa esquina.
 * Medición: scratch `measure_coast_alpha.ps1` (2026-07) sobre pattern1/3/4.
 */
// Esquinas CONVEXAS ("para afuera": agua en dos lados adyacentes): el rincón de tierra hacia el
// centro es un cuadrado 2×2 PISABLE (antes era una "L" de 3 tiles y el 4º del rincón quedaba
// bloqueado aunque es tierra a efectos de caminar). El tile extra es levemente más generoso que
// el alpha ≥50% del sprite, a propósito, para poder pisar/rodear la esquina.
const COAST_LAND_MASKS: Record<string, readonly string[]> = {
  '0_0': ['....', '....', '..XX', '..XX'], // tl — agua arriba e izquierda (2×2 abajo-derecha)
  '4_0': ['....', '....', 'XXXX', 'XXXX'], // t — agua arriba
  '8_0': ['....', '....', 'XX..', 'XX..'], // tr — agua arriba y derecha (2×2 abajo-izquierda)
  '0_4': ['..XX', '..XX', '..XX', '..XX'], // l — agua a la izquierda
  '4_4': ['XXXX', 'XXXX', 'XXXX', 'XXXX'], // c — centro sólido
  '8_4': ['XX..', 'XX..', 'XX..', 'XX..'], // r — agua a la derecha
  '0_8': ['..XX', '..XX', '....', '....'], // bl — agua abajo e izquierda (2×2 arriba-derecha)
  '4_8': ['XXXX', 'XXXX', '....', '....'], // b — agua abajo
  '8_8': ['XX..', 'XX..', '....', '....'], // br — agua abajo y derecha (2×2 arriba-izquierda)
  '20_8': ['..XX', '.XXX', 'XXXX', 'XXXX'], // mordida NW
  '12_8': ['XX..', 'XXX.', 'XXXX', 'XXXX'], // mordida NE
  '20_0': ['XXXX', 'XXXX', '.XXX', '..XX'], // mordida SW
  '12_0': ['XXXX', 'XXXX', 'XXX.', 'XX..'], // mordida SE
};

/** True si el sprite id es de agua (water_1 y variantes futuras). */
export function isWaterSprite(spriteId: string): boolean {
  return spriteId.toLowerCase().includes('water');
}

/** Key canónica de tile para sets/maps de índices. */
export function waterTileKey(x: number, y: number): string {
  return `${x},${y}`;
}

/**
 * Construye el índice de tiles de agua NAVEGABLE de un mapa: Set de "x,y".
 * O(tiles del mapa), pensado para correr una vez al cargar el mapa.
 */
export function buildWaterIndex(mapData: MapData): Set<string> {
  const water = new Set<string>();
  /** Bloques 4×4 marcados tierra COMPLETOS (superficies sólidas/desconocidas). */
  const shoreBlocks = new Set<string>();
  /** Tiles individuales marcados tierra (máscaras de overlays de costa). */
  const shoreTiles = new Set<string>();

  for (const layer of mapData.layers) {
    const isWaterLayer = layer.name === WATER_GROUND_LAYER;
    const isShoreLayer = SHORE_SURFACE_LAYERS.includes(layer.name);
    if (!isWaterLayer && !isShoreLayer) continue;

    for (const tile of layer.tiles) {
      if (isWaterSprite(tile.sprite)) {
        if (isWaterLayer) water.add(waterTileKey(tile.x, tile.y));
        continue;
      }
      if (!isShoreLayer) continue;

      // Overlay de costa de bioma: marcar tierra SOLO donde la máscara de alpha
      // dice que el sprite es sólido (por tile). Los sprites de bloque van
      // anclados al origen del bloque 4×4.
      const m = COAST_ATLAS_RE.exec(tile.sprite);
      const mask = m ? COAST_LAND_MASKS[`${m[1]}_${m[2]}`] : undefined;
      if (mask) {
        for (let dy = 0; dy < SURFACE_BLOCK; dy++) {
          for (let dx = 0; dx < SURFACE_BLOCK; dx++) {
            if (mask[dy][dx] === 'X') shoreTiles.add(waterTileKey(tile.x + dx, tile.y + dy));
          }
        }
        continue;
      }

      // Superficies a nivel de TILE (piezas 9-slice `_1x1`: adoquín, muelles):
      // marcan SOLO su propio tile como tierra (footprint 1×1). Clave para los
      // muelles: la pasarela es caminable exactamente en sus tablas y el agua
      // pegada sigue navegable. Ver docs/WATER_AND_BOATS.md §Muelles.
      if (tile.sprite.endsWith('_1x1')) {
        shoreTiles.add(waterTileKey(tile.x, tile.y));
        continue;
      }

      // Cualquier otra superficie no-agua (pisos de sector, sprites de bloque
      // sin máscara) marca su bloque 4×4 completo como tierra (snap al origen).
      const bx = Math.floor(tile.x / SURFACE_BLOCK) * SURFACE_BLOCK;
      const by = Math.floor(tile.y / SURFACE_BLOCK) * SURFACE_BLOCK;
      shoreBlocks.add(waterTileKey(bx, by));
    }
  }

  // Restar del agua todo tile cubierto por tierra (bloque completo o máscara).
  for (const key of water) {
    if (shoreTiles.has(key)) {
      water.delete(key);
      continue;
    }
    const [x, y] = key.split(',').map(Number);
    const bx = Math.floor(x / SURFACE_BLOCK) * SURFACE_BLOCK;
    const by = Math.floor(y / SURFACE_BLOCK) * SURFACE_BLOCK;
    if (shoreBlocks.has(waterTileKey(bx, by))) water.delete(key);
  }

  return water;
}
